import { useMemo } from 'react';

import { Menu, Divider, Typography, ListItem } from '@mui/material';

import { MarkerData } from 'types/map';

import { ColorPicker } from '../controls/ColorPicker';
import { LinkSelector } from '../controls/LinkSelector';
import { MarkerCreator } from '../controls/MarkerCreator';
import { MarkerImageSelector } from '../controls/MarkerImageSelector';
import { MarkerRemover } from '../controls/MarkerRemover';
import { NameEditor } from '../controls/NameEditor';

type MarkerCtxMenuProps = {
    onClose?: () => void;
    menuAnchor?: {
        top: number;
        left: number;
    };
    markers?: MarkerData[];
    setMarkers?: (markers: MarkerData[]) => void;
    markerID?: string;
};

export const MarkerCtxMenu = ({
    onClose: handleCtxMenuClose = () => undefined,
    menuAnchor,
    markerID,
    markers = [],
    setMarkers = () => undefined,
}: MarkerCtxMenuProps): JSX.Element => {
    const markerProps = useMemo(() => {
        return markers.find((el) => el.id === markerID);
    }, [markerID, markers]);

    const handleMarkerDataChange = (data: Partial<MarkerData>): void => {
        if (markerProps) {
            setMarkers([
                ...markers.filter((el) => el.id !== markerProps.id),
                {
                    ...markerProps,
                    ...data,
                },
            ]);
        }
    };

    const deleteMarker = (): void => {
        setMarkers(markers.filter((el) => el.id !== markerProps?.id));
    };

    return (
        <Menu
            open={Boolean(menuAnchor)}
            onClose={handleCtxMenuClose}
            anchorReference="anchorPosition"
            anchorPosition={menuAnchor}
        >
            <ListItem
                sx={{
                    py: 0.5,
                    maxWidth: 220,
                    display: !markerProps?.name ? 'none' : undefined,
                }}
            >
                <Typography noWrap>{markerProps?.name}</Typography>
            </ListItem>
            <Divider
                sx={{
                    mb: 0.5,
                    display: !markerProps?.name ? 'none' : undefined,
                }}
            />
            <ColorPicker
                onClose={handleCtxMenuClose}
                onColorChange={(color) => handleMarkerDataChange({ color })}
                color={markerProps?.color}
            />
            <NameEditor
                onClose={handleCtxMenuClose}
                onChange={(name) => handleMarkerDataChange({ name })}
                name={markerProps?.name || ''}
            />
            <LinkSelector
                onClose={handleCtxMenuClose}
                onChange={(hrefUuid) =>
                    handleMarkerDataChange({ hrefUuid, imageUuid: undefined })
                }
                value={markerProps?.hrefUuid || ''}
            />
            <MarkerImageSelector
                onClose={handleCtxMenuClose}
                onChange={(imageUuid) =>
                    handleMarkerDataChange({ imageUuid, hrefUuid: undefined })
                }
                value={markerProps?.imageUuid || ''}
            />
            <MarkerCreator
                onClose={handleCtxMenuClose}
                coords={markerProps?.coords}
                onCreate={(coords) => {
                    handleMarkerDataChange({ coords });
                    handleCtxMenuClose();
                }}
            />
            <MarkerRemover
                onClick={() => {
                    deleteMarker();
                    handleCtxMenuClose();
                }}
            />
        </Menu>
    );
};
